import { useEffect, useState, type ReactNode } from 'react';
import { PageActionGate } from '@/components/auth/PageActionGate';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ButtonLink } from '@/components/ui/ButtonLink';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { EmptyState } from '@/components/ui/EmptyState';
import { HrEmployeeEmploymentEditModal } from '@/modules/hr/components/HrEmployeeEmploymentEditModal';
import { HrEmployeeProfileHeader } from '@/modules/hr/components/HrEmployeeProfileHeader';
import { HrSectionTabPanel, HrSectionTabs } from '@/modules/hr/components/HrSectionTabs';
import { hrFormatHours, hrFormatLeaveDays, hrFormatMinutes } from '@/modules/hr/components/hrCalendarUtils';
import { HR_LIFECYCLE_EVENT_OPTIONS, hrStatusBadgeVariant } from '@/modules/hr/components/hrStatusUtils';
import { DatePicker } from '@/components/ui/DatePicker';
import { Select } from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import { StatCard } from '@/components/ui/StatCard';
import { KpiGrid } from '@/components/ui/KpiGrid';
import type {
  Employee360Response,
  HrEmployeeWorkforceDashboard,
  HrEmploymentHistory,
  HrLifecycleEvent,
} from '@/types/hr';
import { cn } from '@/utils/cn';

interface HrEmployeeDetailPanelProps {
  view: Employee360Response | null;
  workforce: HrEmployeeWorkforceDashboard | null;
  lifecycle: HrLifecycleEvent[];
  employmentHistory: HrEmploymentHistory[];
  loading: boolean;
  eventType: string;
  effectiveDate: string;
  onEventTypeChange: (v: string) => void;
  onEffectiveDateChange: (v: string) => void;
  onRecordLifecycle: () => void;
  onEmploymentSaved?: () => void;
}

const DETAIL_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'employment', label: 'Employment' },
  { id: 'time', label: 'Time & leave' },
  { id: 'pay', label: 'Pay & performance' },
  { id: 'documents', label: 'Documents & training' },
] as const;

type DetailTabId = (typeof DETAIL_TABS)[number]['id'];

function DetailSection({
  title,
  description,
  children,
  className,
  actions,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  className?: string;
  actions?: ReactNode;
}) {
  return (
    <section className={cn('card p-4', className)} aria-labelledby={`section-${title.replace(/\s+/g, '-')}`}>
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3
            id={`section-${title.replace(/\s+/g, '-')}`}
            className="text-sm font-semibold text-slate-900 dark:text-slate-100"
          >
            {title}
          </h3>
          {description && <p className="mt-0.5 text-xs text-slate-500">{description}</p>}
        </div>
        {actions}
      </div>
      {children}
    </section>
  );
}

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex justify-between gap-4 border-b border-slate-100 py-2.5 last:border-0 dark:border-slate-800">
      <dt className="text-sm text-slate-500">{label}</dt>
      <dd className="text-right text-sm font-medium text-slate-900 dark:text-slate-100">{value}</dd>
    </div>
  );
}

function DetailLoadingSkeleton() {
  return (
    <div className="space-y-4" aria-busy="true" aria-label="Loading employee profile">
      <div className="card p-5">
        <div className="flex gap-4">
          <Skeleton className="h-14 w-14 shrink-0 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-64" />
            <Skeleton className="h-4 w-40" />
          </div>
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-20 rounded-xl" />
        ))}
      </div>
      <Skeleton className="h-10 w-full rounded-lg" />
      <Skeleton className="h-64 w-full rounded-xl" />
    </div>
  );
}

export function HrEmployeeDetailPanel({
  view,
  workforce,
  lifecycle,
  employmentHistory,
  loading,
  eventType,
  effectiveDate,
  onEventTypeChange,
  onEffectiveDateChange,
  onRecordLifecycle,
  onEmploymentSaved,
}: HrEmployeeDetailPanelProps) {
  const [activeTab, setActiveTab] = useState<DetailTabId>('overview');
  const [employmentEditOpen, setEmploymentEditOpen] = useState(false);

  useEffect(() => {
    setActiveTab('overview');
  }, [view?.employee.id]);

  if (loading) {
    return <DetailLoadingSkeleton />;
  }

  if (!view) {
    return (
      <EmptyState
        title="Select an employee"
        description="Choose someone from the directory or hierarchy to view their full HR profile."
        className="min-h-[360px]"
      />
    );
  }

  const emp = view.employee;
  const subtitle = [
    view.employment?.designationName ?? emp.designation,
    view.employment?.departmentName,
  ]
    .filter(Boolean)
    .join(' · ');

  const headerBadges = [
    workforce?.employmentType && { label: workforce.employmentType, variant: 'info' as const },
    workforce?.lifecycleStage && { label: workforce.lifecycleStage, variant: 'default' as const },
  ].filter(Boolean) as { label: string; variant?: 'default' | 'success' | 'warning' | 'error' | 'info' }[];

  const attendanceColumns: Column<NonNullable<Employee360Response['recentAttendance']>[number]>[] = [
    { key: 'date', header: 'Date', render: (r) => r.date ?? '—' },
    {
      key: 'status',
      header: 'Status',
      render: (r) => <Badge variant={hrStatusBadgeVariant(r.status)}>{r.status}</Badge>,
    },
    {
      key: 'worked',
      header: 'Worked',
      render: (r) => (r.workedMinutes != null ? hrFormatMinutes(r.workedMinutes) : '—'),
    },
  ];

  const leaveColumns: Column<NonNullable<Employee360Response['recentLeaves']>[number]>[] = [
    { key: 'leaveType', header: 'Type' },
    {
      key: 'status',
      header: 'Status',
      render: (r) => <Badge variant={hrStatusBadgeVariant(r.status)}>{r.status}</Badge>,
    },
    { key: 'totalDays', header: 'Days', render: (r) => hrFormatLeaveDays(r.totalDays) },
  ];

  const payrollColumns: Column<NonNullable<Employee360Response['recentPayroll']>[number]>[] = [
    { key: 'runCode', header: 'Run' },
    { key: 'netAmount', header: 'Net amount' },
    {
      key: 'status',
      header: 'Status',
      render: (r) => <Badge variant={hrStatusBadgeVariant(r.status)}>{r.status}</Badge>,
    },
  ];

  const performanceColumns: Column<NonNullable<Employee360Response['performance']>[number]>[] = [
    { key: 'cycleName', header: 'Cycle' },
    { key: 'score', header: 'Score', render: (r) => r.score ?? '—' },
    {
      key: 'status',
      header: 'Status',
      render: (r) => <Badge variant={hrStatusBadgeVariant(r.status)}>{r.status}</Badge>,
    },
  ];

  const expenseColumns: Column<NonNullable<Employee360Response['expenses']>[number]>[] = [
    { key: 'claimNumber', header: 'Claim' },
    { key: 'amount', header: 'Amount' },
    {
      key: 'status',
      header: 'Status',
      render: (r) => <Badge variant={hrStatusBadgeVariant(r.status)}>{r.status}</Badge>,
    },
  ];

  return (
    <div className="space-y-4">
      <HrEmployeeProfileHeader
        firstName={emp.firstName}
        lastName={emp.lastName}
        employeeCode={emp.employeeCode}
        email={emp.email}
        subtitle={subtitle || undefined}
        badges={headerBadges}
        actions={
          <>
            <ButtonLink to={`/hr/workforce?employeeId=${emp.id}`} variant="secondary" size="sm">
              Workforce
            </ButtonLink>
            <ButtonLink to={`/hr/attendance?employeeId=${emp.id}`} variant="secondary" size="sm">
              Attendance
            </ButtonLink>
            <ButtonLink to={`/hr/leaves?employeeId=${emp.id}`} variant="secondary" size="sm">
              Leave
            </ButtonLink>
          </>
        }
      />

      {workforce && (
        <KpiGrid columns={4}>
          <StatCard title="Expected hours" value={hrFormatHours(workforce.expectedHoursThisMonth)} />
          <StatCard title="Worked hours" value={hrFormatHours(workforce.workedHours)} />
          <StatCard title="Employment type" value={workforce.employmentType} />
          <StatCard title="Lifecycle stage" value={workforce.lifecycleStage} />
        </KpiGrid>
      )}

      <HrSectionTabs
        idPrefix="employee-detail"
        tabs={[...DETAIL_TABS]}
        active={activeTab}
        onChange={(id) => setActiveTab(id as DetailTabId)}
      />

      <div className="pt-2">
          <HrSectionTabPanel
            idPrefix="employee-detail"
            tabId="overview"
            active={activeTab === 'overview'}
            className="space-y-4"
          >
            <DetailSection title="Employment summary">
              <dl>
                <DetailRow label="Department" value={view.employment?.departmentName ?? '—'} />
                <DetailRow label="Designation" value={view.employment?.designationName ?? emp.designation ?? '—'} />
                <DetailRow label="Manager" value={view.employment?.managerName ?? workforce?.managerName ?? '—'} />
                <DetailRow
                  label="Employment type"
                  value={view.employment?.employmentType ?? workforce?.employmentType ?? '—'}
                />
              </dl>
            </DetailSection>

            <div className="grid gap-4 lg:grid-cols-2">
              <DetailSection title="Recent attendance" description="Last recorded days">
                <DataTable
                  columns={attendanceColumns}
                  data={view.recentAttendance ?? []}
                  keyField="id"
                  embedded
                  pageSize={5}
                  emptyTitle="No attendance records"
                />
              </DetailSection>
              <DetailSection title="Recent leave" description="Latest leave requests">
                <DataTable
                  columns={leaveColumns}
                  data={view.recentLeaves ?? []}
                  keyField="id"
                  embedded
                  pageSize={5}
                  emptyTitle="No leave records"
                />
              </DetailSection>
            </div>

            {(view.expenses ?? []).length > 0 && (
              <DetailSection title="Recent expenses">
                <DataTable
                  columns={expenseColumns}
                  data={view.expenses ?? []}
                  keyField="claimId"
                  embedded
                  pageSize={5}
                  emptyTitle="No expense claims"
                />
              </DetailSection>
            )}
          </HrSectionTabPanel>

          <HrSectionTabPanel
            idPrefix="employee-detail"
            tabId="employment"
            active={activeTab === 'employment'}
            className="space-y-4"
          >
            <DetailSection
              title="Employment details"
              actions={
                <PageActionGate action="edit">
                  <Button size="sm" variant="secondary" onClick={() => setEmploymentEditOpen(true)}>
                    Edit employment
                  </Button>
                </PageActionGate>
              }
            >
              <dl>
                <DetailRow label="Department" value={view.employment?.departmentName ?? '—'} />
                <DetailRow label="Designation" value={view.employment?.designationName ?? emp.designation ?? '—'} />
                <DetailRow label="Grade" value={view.employment?.gradeName ?? '—'} />
                <DetailRow label="Manager" value={view.employment?.managerName ?? workforce?.managerName ?? '—'} />
                <DetailRow
                  label="Employment type"
                  value={view.employment?.employmentType ?? workforce?.employmentType ?? '—'}
                />
              </dl>
              <HrEmployeeEmploymentEditModal
                open={employmentEditOpen}
                employeeId={emp.id}
                employment={view.employment}
                workforceEmploymentType={workforce?.employmentType}
                onClose={() => setEmploymentEditOpen(false)}
                onSaved={() => onEmploymentSaved?.()}
              />
            </DetailSection>

            <DetailSection title="Lifecycle history" description="Stage changes and HR events">
              <ul className="mb-4 space-y-2" aria-label="Lifecycle events">
                {lifecycle.length === 0 && <li className="text-sm text-slate-500">No lifecycle events recorded.</li>}
                {lifecycle.map((e) => (
                  <li
                    key={e.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 px-3 py-2 text-sm dark:border-slate-800"
                  >
                    <span className="font-medium">{e.eventType.replace(/_/g, ' ')}</span>
                    <span className="text-slate-500">{e.effectiveDate}</span>
                    {e.previousStage && e.newStage && (
                      <span className="w-full text-xs text-slate-500">
                        {e.previousStage} → {e.newStage}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
              <div className="grid gap-3 sm:grid-cols-2">
                <Select
                  label="Event type"
                  value={eventType}
                  onChange={onEventTypeChange}
                  options={HR_LIFECYCLE_EVENT_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                />
                <DatePicker label="Effective date" value={effectiveDate} onChange={onEffectiveDateChange} />
              </div>
              <Button className="mt-3" size="sm" onClick={onRecordLifecycle} disabled={!effectiveDate}>
                Record lifecycle event
              </Button>
            </DetailSection>

            {employmentHistory.length > 0 && (
              <DetailSection title="Employment history">
                <div className="grid gap-3 sm:grid-cols-2">
                  {employmentHistory.map((h) => (
                    <div
                      key={h.id}
                      className="rounded-lg border border-slate-100 p-3 text-sm dark:border-slate-800"
                    >
                      <div className="font-medium">{h.employmentTypeCode}</div>
                      <div className="text-slate-500">
                        {h.startDate}
                        {h.endDate ? ` → ${h.endDate}` : ' → present'}
                      </div>
                      {h.reason && <div className="mt-1 text-slate-600 dark:text-slate-400">{h.reason}</div>}
                    </div>
                  ))}
                </div>
              </DetailSection>
            )}
          </HrSectionTabPanel>

          <HrSectionTabPanel
            idPrefix="employee-detail"
            tabId="time"
            active={activeTab === 'time'}
            className="space-y-4"
          >
            <DetailSection title="Attendance history">
              <DataTable
                columns={attendanceColumns}
                data={view.recentAttendance ?? []}
                keyField="id"
                embedded
                pageSize={8}
                emptyTitle="No attendance records"
                emptyDescription="Attendance will appear here once recorded."
              />
            </DetailSection>
            <DetailSection title="Leave history">
              <DataTable
                columns={leaveColumns}
                data={view.recentLeaves ?? []}
                keyField="id"
                embedded
                pageSize={8}
                emptyTitle="No leave records"
                emptyDescription="Leave requests will appear here once submitted."
              />
            </DetailSection>
          </HrSectionTabPanel>

          <HrSectionTabPanel
            idPrefix="employee-detail"
            tabId="pay"
            active={activeTab === 'pay'}
            className="space-y-4"
          >
            <DetailSection title="Payroll runs">
              <DataTable
                columns={payrollColumns}
                data={view.recentPayroll ?? []}
                keyField="runId"
                embedded
                pageSize={8}
                emptyTitle="No payroll records"
              />
            </DetailSection>
            <DetailSection title="Performance reviews">
              <DataTable
                columns={performanceColumns}
                data={view.performance ?? []}
                keyField="reviewId"
                embedded
                pageSize={8}
                emptyTitle="No performance reviews"
              />
            </DetailSection>
          </HrSectionTabPanel>

          <HrSectionTabPanel
            idPrefix="employee-detail"
            tabId="documents"
            active={activeTab === 'documents'}
            className="space-y-4"
          >
            <DetailSection title="Documents">
              {(view.documents ?? []).length === 0 ? (
                <p className="text-sm text-slate-500">No documents on file.</p>
              ) : (
                <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                  {(view.documents ?? []).map((d) => (
                    <li key={d.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5 text-sm">
                      <span className="font-medium">{d.documentName}</span>
                      {d.expiryDate && (
                        <Badge variant={hrStatusBadgeVariant('warning')}>Expires {d.expiryDate}</Badge>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </DetailSection>

            <div className="grid gap-4 lg:grid-cols-2">
              <DetailSection title="Training">
                {(view.training ?? []).length === 0 ? (
                  <p className="text-sm text-slate-500">No training records.</p>
                ) : (
                  <ul className="space-y-2">
                    {(view.training ?? []).map((t) => (
                      <li
                        key={t.courseId}
                        className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 px-3 py-2 text-sm dark:border-slate-800"
                      >
                        <span>{t.title}</span>
                        <Badge variant={hrStatusBadgeVariant(t.completionStatus)}>{t.completionStatus}</Badge>
                      </li>
                    ))}
                  </ul>
                )}
              </DetailSection>

              <DetailSection title="Assigned assets">
                {(view.assets ?? []).length === 0 ? (
                  <p className="text-sm text-slate-500">No assets assigned.</p>
                ) : (
                  <ul className="space-y-2">
                    {(view.assets ?? []).map((a) => (
                      <li
                        key={a.id}
                        className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 px-3 py-2 text-sm dark:border-slate-800"
                      >
                        <span>
                          {a.assetType}
                          {a.assetTag ? ` · ${a.assetTag}` : ''}
                        </span>
                        <Badge variant={hrStatusBadgeVariant(a.status)}>{a.status}</Badge>
                      </li>
                    ))}
                  </ul>
                )}
              </DetailSection>
            </div>
          </HrSectionTabPanel>
      </div>
    </div>
  );
}
